import QuestionCircleTwoTone from '@ant-design/icons/lib/icons/QuestionCircleTwoTone';
import { App, Input, InputNumber, Layout, Select, Space, Switch, Table, Tag, Tooltip } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Content, Header } from 'antd/lib/layout/layout';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useDepartments } from '@/common/useDepartments';
import { useTableSkeleton } from '@/components/skeletons/useTableSkeleton';
import { useAuth } from '@/core/providers/AuthFirebaseProvider';
import type { District, User } from '@/generated/graphql-types';
import { _limitedUsers } from '../districts-new/components/DistrictsTableViewNew';
import { DistrictsInputView } from './components/DistrictsInputView';
import { DriverWithDistrictsView } from './components/DriverWithDistrictsView';
import {
  useDeleteDriverDistrictLegacy,
  useGetDistrictsWithDriversLegacy,
  useGetDriversWithDistrictsLegacy,
  useInsertDriverDistrictLegacy,
  useUpdateDistrictDeliveryDaysLegacy,
  useUpdateDistrictLoadFactorLegacy,
  useUpdateDistrictOffDaysLegacy,
} from './query/districts.legacy.hooks';
import { getUnusedDistrictsForDriver } from './utils/driversTableData';

interface GraphQLUser {
  __typename?: 'user';
  id: string;
  name: string;
  driver_districts: Array<{
    __typename?: 'driver_district';
    id: number;
    district: {
      __typename?: 'district';
      id: string;
      name?: string | null;
      enabled?: boolean | null;
    };
  }>;
}

interface DriverRowState {
  districts: District[];
  inputVisible: boolean;
  isLoading: boolean;
  inputValue: string;
  unusedDistricts: District[];
}

interface DriverTableRow {
  key: number;
  drivers: string;
  data: string;
  districts: District[];
  unusedDistricts: District[];
}

const offDaysOptions = [
  { label: 'Mon', value: 1 },
  { label: 'Tue', value: 2 },
  { label: 'Wed', value: 3 },
  { label: 'Thu', value: 4 },
  { label: 'Fri', value: 5 },
  { label: 'Sat', value: 6 },
  { label: 'Sun', value: 7 },
];

export const DistrictsPageView: React.FC = () => {
  const { message } = App.useApp();
  const { userDataParams } = useAuth();
  const [switchTableChecked, setSwitchTableChecked] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [debouncedSearchText, setDebouncedSearchText] = useState<string>('');
  const { selectedDepartmentId: departmentId } = useDepartments();
  const [rowStates, setRowStates] = useState<Record<string, DriverRowState>>({});

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchText(searchText), 300);
    return () => clearTimeout(t);
  }, [searchText]);

  const variables = useMemo(
    () => ({
      departmentId,
      searchText: debouncedSearchText ? `%${debouncedSearchText}%` : undefined,
    }),
    [departmentId, debouncedSearchText]
  );

  const { data: driversView, isLoading: loadingDrivers } =
    useGetDriversWithDistrictsLegacy(variables);
  const { data: districtsView, isLoading: loadingDistricts } =
    useGetDistrictsWithDriversLegacy(variables);

  const { mutateAsync: insertDriverDistrict } = useInsertDriverDistrictLegacy();
  const { mutateAsync: deleteDriverDistrict } = useDeleteDriverDistrictLegacy();
  const { mutateAsync: updateLoadFactor } = useUpdateDistrictLoadFactorLegacy();
  const { mutateAsync: updateDeliveryDays } = useUpdateDistrictDeliveryDaysLegacy();
  const { mutateAsync: updateOffDays } = useUpdateDistrictOffDaysLegacy();

  const driversTableData = useMemo(() => {
    const districts = driversView?.district ?? [];
    const rows = (driversView?.user ?? []).map((driver: GraphQLUser, index: number) => ({
      key: index,
      drivers: driver.name,
      data: driver.id,
      districts: (driver.driver_districts as Array<{ district: unknown }>).map((dd) => dd.district),
      unusedDistricts: getUnusedDistrictsForDriver(
        districts as Array<{ id: string }>,
        driver.driver_districts as Array<{ district: { id: string } }>
      ),
    }));
    return { rows, allDistricts: districts };
  }, [driversView]);

  useEffect(() => {
    if (!driversTableData?.rows) return;
    setRowStates((prev) => {
      const next: Record<string, DriverRowState> = { ...prev };
      (driversTableData.rows as any).forEach((row: any) => {
        if (!next[row.data]) {
          next[row.data] = {
            districts: row.districts,
            inputVisible: false,
            isLoading: false,
            inputValue: '',
            unusedDistricts: row.unusedDistricts,
          };
        } else {
          next[row.data] = {
            ...next[row.data],
            districts: row.districts,
            unusedDistricts: row.unusedDistricts,
          };
        }
      });
      return next;
    });
  }, [driversTableData.rows]);

  const districtsTableData = useMemo(() => {
    const list = districtsView?.district ?? [];
    const secondTableRowStates: Record<
      string,
      { drivers: { driverName: string; driverId: string }[] }
    > = {};
    (list as any).forEach((d: any) => {
      secondTableRowStates[d.id] = {
        drivers: d.driver_districts.map((x: { driver: User; driverId: string }) => ({
          driverName: x.driver.name,
          driverId: x.driverId,
        })),
      };
    });
    const rows = (list as any).map((district: any, index: number) => ({
      key: index,
      districts: district.name,
      data: district.id,
      districtEnabled: { enabled: district.enabled, districtId: district.id },
      loadFactor: { loadFactor: district.loadFactor, districtId: district.id },
      offDays: { offDays: district.offDays, districtId: district.id },
      deliveryDays: { deliveryDays: district.deliveryDays, districtId: district.id },
    }));
    return { rows, secondTableRowStates };
  }, [districtsView]);

  const driversColumns = [
    { title: 'Drivers', dataIndex: 'drivers' },
    {
      title: 'Districts',
      dataIndex: 'data',
      render: (driverId: string, _record: DriverTableRow) => (
        <DistrictsInputView
          state={{
            rowStates,
            handleInputConfirm: async (districtId: string, dId: string, label?: string) => {
              setRowStates((prev) => ({
                ...prev,
                [dId]: { ...prev[dId], inputValue: label ?? '', isLoading: true },
              }));

              try {
                await insertDriverDistrict({ driverId: dId, districtId });
              } catch (_e) {
                message.error('Failed to add district');
              } finally {
                setRowStates((prev) => ({
                  ...prev,
                  [dId]: { ...prev[dId], inputValue: '', isLoading: false, inputVisible: false },
                }));
              }
            },
            handleInputChange: (value: string, dId: string) => {
              setRowStates((prev) => ({
                ...prev,
                [dId]: { ...prev[dId], inputValue: value },
              }));
            },
            showInput: (dId: string) => {
              setRowStates((prev) => ({
                ...prev,
                [dId]: { ...prev[dId], inputVisible: true },
              }));
            },
            handleClose: async (_district: District, dId: string) => {
              try {
                await deleteDriverDistrict({ districtId: _district.id, driverId: dId });
              } catch (_e) {
                message.error('Failed to remove district');
              }
            },
          }}
          driverId={driverId}
        />
      ),
    },
  ];

  const districtsColumns = [
    { title: 'Districts', dataIndex: 'districts' },
    {
      title: 'Drivers',
      dataIndex: 'data',
      render: (districtId: string) => (
        <DriverWithDistrictsView
          store={{
            secondTableRowStates: districtsTableData.secondTableRowStates,
            deleteDistrictDriver: async (dId: string, driverId: string) => {
              try {
                await deleteDriverDistrict({ districtId: dId, driverId });
              } catch (_e) {
                message.error('Failed to remove driver');
              }
            },
          }}
          districtId={districtId}
        />
      ),
    },
    {
      title: 'Load Factor',
      dataIndex: 'loadFactor',
      render: (districtProps: { loadFactor: number; districtId: string }) => (
        <InputNumber
          min={1}
          max={7}
          defaultValue={districtProps.loadFactor}
          onChange={(val) => {
            if (val != null)
              updateLoadFactor({
                loadFactor: Number(val),
                districtId: districtProps.districtId,
              }).catch(() => message.error('Failed to update load factor'));
          }}
        />
      ),
    },
    {
      title: (
        <span>
          Schedule limitation&nbsp;
          <Tooltip title="Only some people have access to this field">
            <QuestionCircleTwoTone />
          </Tooltip>
        </span>
      ),
      dataIndex: 'deliveryDays',
      render: (districtProps: { deliveryDays: number; districtId: string }) => (
        <InputNumber
          disabled={!_limitedUsers.includes(userDataParams?.email || '')}
          min={1}
          max={7}
          defaultValue={districtProps.deliveryDays}
          onChange={(val) => {
            if (val != null)
              updateDeliveryDays({
                deliveryDays: Number(val),
                districtId: districtProps.districtId,
              }).catch(() => message.error('Failed to update delivery days'));
          }}
        />
      ),
    },
    {
      title: 'Off days',
      dataIndex: 'offDays',
      render: (districtProps: { offDays: number[]; districtId: string }) => (
        <Select
          mode="multiple"
          allowClear
          style={{ width: '100%' }}
          placeholder="Select days"
          defaultValue={districtProps?.offDays?.length >= 1 ? districtProps.offDays : undefined}
          options={offDaysOptions}
          onChange={(offDays: number[]) =>
            updateOffDays({ offDays, districtId: districtProps.districtId }).catch(() =>
              message.error('Failed to update off days')
            )
          }
        />
      ),
    },
    {
      title: 'Enabled',
      dataIndex: 'districtEnabled',
      render: (districtProps: { districtId: string; enabled: boolean }) => (
        <Tag color={districtProps.enabled ? 'green' : 'red'}>
          {districtProps.enabled ? 'Enabled' : 'Disabled'}
        </Tag>
      ),
    },
  ];

  const driversTableSkeleton = useTableSkeleton(
    driversColumns as ColumnsType,
    driversTableData.rows as DriverTableRow[],
    loadingDrivers
  );
  const districtsTableSkeleton = useTableSkeleton(
    districtsColumns as ColumnsType,
    districtsTableData.rows,
    loadingDistricts
  );

  return (
    <Layout>
      <Header
        style={{
          backgroundColor: '#1890ff',
        }}
      >
        <Switch
          className="app-switch"
          style={{
            backgroundColor: switchTableChecked ? '#0050b3' : '#096dd9',
          }}
          checkedChildren="District with drivers"
          unCheckedChildren="Driver with districts"
          checked={switchTableChecked}
          onChange={(checked) => setSwitchTableChecked(checked)}
        />
      </Header>
      <Content style={{ padding: 16 }}>
        <Space orientation="vertical" style={{ width: '100%' }}>
          <Input.Search
            placeholder={switchTableChecked ? 'Search districts...' : 'Search drivers...'}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
        </Space>
        {!switchTableChecked ? (
          <Table
            className="app-table app-table--wrap"
            columns={driversTableSkeleton.columns}
            dataSource={driversTableSkeleton.dataSource}
            loading={driversTableSkeleton.loading}
          />
        ) : (
          <Table
            className="app-table app-table--wrap"
            columns={districtsTableSkeleton.columns}
            dataSource={districtsTableSkeleton.dataSource}
            loading={districtsTableSkeleton.loading}
          />
        )}
      </Content>
    </Layout>
  );
};
